// @common/components/cookie-consent/cookie-consent.component.js

/**
 * @name CookieConsentController
 * @description Controller for the cookie-consent component.
 */
class CookieConsentController {
  // Ensure all dependencies are correctly injected for minification
  static $inject = ["AppService", "$window", "$timeout"];

  constructor(AppService, $window, $timeout) {
    // Use angular.extend for older AngularJS projects, or Object.assign if supported
    // angular.extend(this, { AppService, $window, $timeout });
    Object.assign(this, { AppService, $window, $timeout });

    // Properties initialized in constructor
    this.cookieName = `_${
      AppService.config.pubPath == "crsld" ? "crsld" : AppService.config.appName
    }cs`;
    this.pendingConsent = null;
  }

  /**
   * AngularJS component life-cycle hook, replaces async $onInit
   */
  $onInit() {
    // Use .then/.catch for promises instead of async/await for wider AngularJS compatibility
    this.waitForClarity()
      .then(() => {
        if (this.pendingConsent) {
          this.syncConsentAcrossTools(this.pendingConsent);
        }
      })
      .catch((error) => {
        // console.warn("Clarity wait failed:", error); // Optional: log failure
      });

    // Start the cookie check and display mechanism
    this.checkAndDisplayConsent();
  }

  /**
   * Gets a cookie value by name.
   * @param {string} n - The cookie name.
   * @returns {string} The cookie value or an empty string.
   */
  getCookie(n) {
    // Updated to be compatible with older JS environments and concise
    const nameEQ = n + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return "";
  }

  /**
   * Sets a cookie.
   * @param {string} n - The cookie name.
   * @param {string} v - The cookie value.
   * @param {number} d - Days until expiry.
   */
  setCookie(n, v, d) {
    const expirationDate = new Date(Date.now() + d * 864e5).toUTCString();
    document.cookie = `${n}=${v};expires=${expirationDate};path=/`;
  }

  /**
   * Waits for the Microsoft Clarity function to be available on the window object.
   * @param {number} [max=8000] - Max wait time in ms.
   * @param {number} [int=300] - Check interval in ms.
   * @returns {Promise<number|string>} Resolves on success, rejects on timeout.
   */
  waitForClarity(max = 8e3, int = 300) {
    return new Promise((res, rej) => {
      const startTime = Date.now();
      const check = () => {
        // Check if the clarity function is on the window object
        if (typeof this.$window.clarity === "function") {
          res(1);
        } else if (Date.now() - startTime > max) {
          rej("timeout");
        } else {
          // Use $timeout for Angular context awareness
          this.$timeout(check, int, false); // false for `invokeApply`
        }
      };
      check();
    });
  }

  /**
   * Sets the consent with Microsoft Clarity.
   * @param {("granted"|"denied")} t - The consent type.
   */
  syncConsentAcrossTools(type = "granted") {
    // Clarity
    if (typeof this.$window.clarity === "function") {
      this.$window.clarity("consentv2", {
        ad_Storage: type,
        analytics_Storage: type,
      });
    } else {
      this.pendingConsent = type;
    }

    // Facebook
    if (typeof this.$window.fbq === "function") {
      this.$window.fbq("consent", type === "granted" ? "grant" : "revoke");
    }

    // Google Analytics / Ads
    if (typeof this.$window.gtag === "function") {
      this.$window.gtag("consent", "update", {
        ad_storage: type,
        analytics_storage: type,
      });
    }

    if (window.__DEBUG_CONSENT)
      console.log(`[Consent] Synced to ${type.toUpperCase()} across tools`);
  }

  /**
   * Checks for existing consent cookie and displays the UI if consent is missing.
   * Uses $timeout to safely integrate with the AngularJS digest cycle.
   */
  checkAndDisplayConsent() {
    const saved = this.getCookie(this.cookieName);
    const currentConsent = saved || "granted";

    // Google default (Consent Mode)
    if (typeof window.gtag === "function") {
      window.gtag("consent", "default", {
        ad_storage: currentConsent,
        analytics_storage: currentConsent,
      });
    }
    // Apply consent to all tools
    this.syncConsentAcrossTools(currentConsent);

    // If consent already saved, no banner
    if (saved) return;
    // No consent found, schedule the UI to appear after a short delay
    const onTimeout = () => {
      // Double check for cookie just in case it was set quickly elsewhere
      const currentSaved = this.getCookie(this.cookieName);
      if (currentSaved) return this.syncConsentAcrossTools(currentSaved);

      // Set default 'granted' consent immediately for trackers like Clarity,
      // then display the banner for the user to change their mind.
      this.syncConsentAcrossTools("granted");

      const html = `
                <div class="disclaimer position-fixed bottom-0 end-0 bg-white rounded-3 border mb-2 mx-2 p-3 w-auto d-none" style="max-width:510px; z-index:11">
                    <div class="d-flex align-items-top">
                        <div class="flex-shrink-0">
                            <svg width="90" height="90" viewBox="0 0 24 24" aria-hidden="true" class="text-primary">
                                <defs>
                                    <mask id="cookieMaskSolid24">
                                        <rect width="24" height="24" fill="#fff"/>
                                        <circle cx="18" cy="6.5" r="3.6" fill="#000"/>
                                        <circle cx="9" cy="12" r="1.2" fill="#000"/>
                                        <circle cx="13.2" cy="15" r="1.1" fill="#000"/>
                                        <circle cx="16" cy="10.2" r="1.0" fill="#000"/>
                                        <circle cx="10" cy="8.3" r="0.9" fill="#000"/>
                                    </mask>
                                </defs>
                                <circle cx="12" cy="12" r="9" fill="currentColor" mask="url(#cookieMaskSolid24)"/>
                            </svg>
                        </div>
                        <div class="flex-grow-1 ms-2">
                            <p class="mb-0">We use cookies to personalize your experience. By continuing to browse, you consent to ${
                              this.learnmore
                                ? `<a href="${this.learnmore}" target="_blank" rel="noopener noreferrer">our use of cookies</a>`
                                : `our use of cookies`
                            }.</p>
                            <button type="button" name="granted" class="btn btn-primary btn-sm py-3 px-4 btn-cookie me-2" style="font-size:0.9375rem">Got It</button>
                            <button type="button" name="denied" class="btn btn-secondary btn-sm py-3 px-4 btn-cookie" style="font-size:0.9375rem">Reject</button>
                        </div>
                    </div>
                </div>`;

      // Create and insert the banner element
      const wrapper = document.createElement("div");
      wrapper.innerHTML = html.trim();
      const banner = wrapper.firstElementChild;
      document.body.appendChild(banner);
      banner.classList.remove("d-none");

      // Attach event listener for user action
      banner.addEventListener("click", (e) => {
        const target = e.target.closest("[name]");
        if (!target) return;

        const consentValue = target.name; // 'granted' or 'denied'

        // Update consent status and set the cookie
        this.syncConsentAcrossTools(consentValue);
        this.setCookie(this.cookieName, consentValue, 365);

        // Remove the banner from the DOM
        banner.remove();
      });
    };

    // Use $timeout to trigger display after 2 seconds
    this.$timeout(onTimeout, 2e3);
  }
}

/**
 * @name cookieconsent
 * @description Angular component definition object.
 */
class CookieConsentComponent {
  constructor() {
    this.bindings = {
      learnmore: "@?",
    };
    this.controller = CookieConsentController;
    // The component doesn't need a template since it dynamically injects the HTML
    // However, for best practice, an empty template can be defined or simply omit it.
    // template: '<div></div>'
  }
}

export default angular
  .module("cookieconsent.component", [])
  .component("cookieconsent", new CookieConsentComponent()).name;
